import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { ReleasePost } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { useGetReleasePlanQuery } from '../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { Panel, PanelBody, PanelFoot, PanelHead } from '../../../components/primitives/Panel';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { StudioActionError } from '../../studio/StudioActionError';
import { localDay, PlanMode } from '../plan/planMode';
import { useReleasePlanChannels } from '../plan/useReleasePlanChannels';
import { usePlanGapActions } from '../plan/usePlanGapActions';
import { planDayTitle, planDayDate } from '../plan/rolloutDayCopy';
import { ReleaseTab } from '../releaseTabProps';
import { overviewMessages } from './overviewMessages';
import { OverviewPostRow } from './OverviewPostRow';
import { NoDateNote } from './NoDateNote';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Overview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PlannedPanelProps {
  accountId: number;
  item: CatalogItem;
  mode: PlanMode;
  posts: ReleasePost[];
  onOpenTab: (tab: ReleaseTab) => void;
  onOpenCalendar: () => void;
}

const SHOWN_ROWS = 4;
const PLANNED: ReadonlyArray<string> = ['draft', 'scheduled', 'rendering', 'ready', 'publishing', 'awaitingManualPost'];

/**
 * Planned (design b1, b2, b3): the next 4 drafts and scheduled posts of the release and its gaps
 * (rollout days with no post), "Add N drafts" (a missing kit asset becomes a Studio job first) and
 * the Calendar. A released song shows its push posts and "Plan a push"; a song with no date asks for
 * the date in the Catalog.
 */
const PlannedPanelComponent: React.FC<PlannedPanelProps> = (props) => {
  const { accountId, item, mode, posts, onOpenTab, onOpenCalendar } = props;
  const intl = useIntl();
  const { platforms } = useReleasePlanChannels(accountId);
  const plan = useGetReleasePlanQuery(
    { accountId, catalogItemUuid: item.uuid, kind: 'release', platforms },
    { skip: mode !== 'release' }
  );
  const today = useMemo(() => localDay(new Date()), []);
  const planned = useMemo(
    () =>
      posts
        .filter((post) => PLANNED.includes(post.status) && post.scheduledAt.slice(0, 10) >= today)
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)),
    [posts, today]
  );
  const gaps = useMemo(() => (plan.data?.days ?? []).filter((day) => day.gap), [plan.data]);
  const actions = usePlanGapActions({ accountId, catalogItemUuid: item.uuid, kind: 'release', platforms, days: gaps });
  const rows = useMemo(() => {
    const entries = [
      ...planned.map((post) => ({ at: post.scheduledAt.slice(0, 10), post, gap: null })),
      ...gaps.map((day) => ({ at: day.date, post: null, gap: day })),
    ].sort((a, b) => a.at.localeCompare(b.at));
    return entries;
  }, [gaps, planned]);

  if (mode === 'noDate') {
    return (
      <Panel as="section" data-testid="release-planned">
        <PanelHead
          title={intl.formatMessage(overviewMessages.plannedTitle)}
          right={<span className={styles.meta}>{intl.formatMessage(overviewMessages.nothingPlanned)}</span>}
        />
        <PanelBody>
          <NoDateNote catalogItemUuid={item.uuid} />
        </PanelBody>
      </Panel>
    );
  }

  const isPush = mode === 'push';
  const meta = isPush
    ? intl.formatMessage(overviewMessages.plannedOnly, { planned: planned.length })
    : intl.formatMessage(overviewMessages.plannedMetaDays, { planned: planned.length, gaps: gaps.length });
  const lastPlanned = planned[planned.length - 1];
  const hidden = rows.length - SHOWN_ROWS;

  return (
    <Panel as="section" data-testid="release-planned">
      <PanelHead
        title={intl.formatMessage(isPush ? overviewMessages.plannedPushTitle : overviewMessages.plannedTitle)}
        subtitle={intl.formatMessage(isPush ? overviewMessages.plannedPushSubtitle : overviewMessages.plannedSubtitle)}
        right={
          <span className={styles.meta}>
            {meta}
            <button type="button" className={styles.textLink} onClick={() => onOpenTab('plan')}>
              {intl.formatMessage(overviewMessages.openPlan)}
            </button>
          </span>
        }
      />
      {rows.length > 0 && (
        <PanelBody>
          <div className={styles.postList}>
            {rows.slice(0, SHOWN_ROWS).map((row) =>
              row.post !== null ? (
                <OverviewPostRow key={row.post.uuid} post={row.post} />
              ) : (
                <Panel nested key={`gap-${row.gap?.key}`} className={`${styles.postRow} ${styles.gapRow}`}>
                  <span className={styles.postText}>
                    <b className={styles.postTitle}>{row.gap === null ? '' : planDayTitle(intl, row.gap)}</b>
                    <small className={styles.postWhen}>
                      {`${planDayDate(intl, row.at)} · ${intl.formatMessage(overviewMessages.noPostYet)}`}
                    </small>
                  </span>
                  <button
                    type="button"
                    className={styles.textLink}
                    disabled={actions.drafts.isAdding}
                    onClick={() => row.gap !== null && actions.addDay(row.gap)}
                  >
                    {intl.formatMessage(overviewMessages.addDraft)}
                  </button>
                </Panel>
              )
            )}
          </div>
        </PanelBody>
      )}
      <PanelFoot>
        <span className={styles.footText}>
          {hidden > 0 && intl.formatMessage(overviewMessages.moreAfter, { count: hidden })}
          {hidden <= 0 &&
            isPush &&
            lastPlanned !== undefined &&
            intl.formatMessage(overviewMessages.nothingAfter, {
              date: planDayDate(intl, lastPlanned.scheduledAt.slice(0, 10)),
            })}
          {actions.drafts.result !== null &&
            ` ${intl.formatMessage(overviewMessages.draftsAdded, { count: actions.drafts.result.created })}`}
          {(actions.drafts.result?.queued?.length ?? 0) > 0 &&
            ` ${intl.formatMessage(overviewMessages.assetsQueued, { count: actions.drafts.result?.queued?.length ?? 0 })}`}
        </span>
        <SMLButton onClick={onOpenCalendar}>{intl.formatMessage(overviewMessages.openCalendar)}</SMLButton>
        {isPush ? (
          <SMLButton onClick={() => onOpenTab('plan')}>{intl.formatMessage(overviewMessages.planPush)}</SMLButton>
        ) : (
          gaps.length > 0 && (
            <SMLButton disabled={actions.drafts.isAdding || platforms.length === 0} onClick={actions.addAll}>
              {intl.formatMessage(overviewMessages.addDrafts, { count: gaps.length })}
            </SMLButton>
          )
        )}
      </PanelFoot>
      {actions.drafts.error !== null && <StudioActionError feedback={actions.drafts.error} />}
    </Panel>
  );
};

export const PlannedPanel = TypedMemo(PlannedPanelComponent);
